import { useContext, useState, useEffect, useRef } from "react"
import classNames from "classnames"

import { getPageOffsets } from "web/services/browser"

import { DataTableContext } from "./DataTable"
import ColumnSettings from "./ColumnSettings"

export default function TitleCell({ column }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const {
    canMoveColumns,
    canGroupTable,
    onSort,
    onGroup,
    onResizeColumn,
    onMoveColumn,
    onFrozenFieldChanged,
    sortColumn,
    sortDesc,
    groupColumn,
    secondGroupColumn,
    enableFrozenColumns,
    isCompact,
  } = useContext(DataTableContext)

  // ref needed because columnWidths is used in a DOM callback
  const columnRef = useRef()
  columnRef.current = column

  const cellRef = useRef()
  const dragCounter = useRef(0)
  const resizeBarStatus = useRef()

  const isGroupColumn = [groupColumn, secondGroupColumn].includes(
    column.dataKey
  )

  const isDataCol = column.type === "data"
  const canMoveColumn = canMoveColumns && isDataCol
  const canResizeColumn = isDataCol

  function stopPropagation(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  useEffect(() => {
    function handleMouseUp(e) {
      if (resizeBarStatus.current) {
        stopPropagation(e)

        const { element, currentCellLeft, xMin, xMax } = resizeBarStatus.current
        const xEffective = Math.min(Math.max(xMin, e.pageX), xMax)
        onResizeColumn(columnRef.current, xEffective - currentCellLeft)

        element.remove()
        resizeBarStatus.current = undefined
      }
    }

    function handleMouseMove(e) {
      if (resizeBarStatus.current) {
        stopPropagation(e)

        const { element, xMin, xMax } = resizeBarStatus.current
        if (xMin < e.pageX && e.pageX < xMax) {
          element.style.left = `${e.pageX}px`
        }
      }
    }

    window.addEventListener("mouseup", handleMouseUp)
    window.addEventListener("mousemove", handleMouseMove)

    return () => {
      window.removeEventListener("mouseup", handleMouseUp)
      window.removeEventListener("mousemove", handleMouseMove)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onResizeColumn])

  const dropColumnClasses = ["border-l-2", "border-dotted", "border-neutral-6"]

  function handleDragEnter() {
    dragCounter.current++
    cellRef.current.classList.add(...dropColumnClasses)
  }

  function handleDragLeave() {
    dragCounter.current--
    if (dragCounter.current === 0) {
      cellRef.current.classList.remove(...dropColumnClasses)
    }
  }

  function handleDragEnd() {
    removeDragStyles()
  }

  function handleDrop(e) {
    const sourceColumn = JSON.parse(e.dataTransfer.getData("text/plain"))
    onMoveColumn(sourceColumn, column)

    removeDragStyles()
  }

  function removeDragStyles() {
    dragCounter.current = 0
    cellRef.current.classList.remove(...dropColumnClasses)
  }

  function handleDragStart(e) {
    setIsMenuOpen(false)
    e.dataTransfer.setData("text/plain", JSON.stringify(column))
  }

  function handleResizeStart(e) {
    stopPropagation(e)

    const tableEl =
      e.target.parentElement.parentElement.parentElement.parentElement
        .parentElement

    const tableOffsets = getPageOffsets(tableEl)
    const cellOffsets = getPageOffsets(cellRef.current)

    const resizeBar = document.createElement("div")
    resizeBar.classList.add("absolute", "w-0.5", "bg-neutral-6", "z-[60]")
    resizeBar.style.top = "0px"

    resizeBar.style.left = `${cellOffsets.right}px`
    resizeBar.style.top = `${cellOffsets.top}px`

    resizeBar.style.height = `${tableOffsets.height - 4}px`

    document.body.appendChild(resizeBar)

    resizeBarStatus.current = {
      element: resizeBar,
      xMin: cellOffsets.left + 100,
      xMax: tableOffsets.right + 100,
      currentCellLeft: cellOffsets.left,
    }
  }

  function handleSort(e, sortDirection) {
    e.stopPropagation()
    if (isDataCol) onSort(column.dataKey, sortDirection)
  }

  const cellClasses = classNames(
    "group/cell bg-white center content-center items-center flex h-full",
    {
      "min-h-10": !isCompact,
      "min-h-9": isCompact,
      "cursor-move": canMoveColumn,
      "pr-0": canResizeColumn,
      "border-r border-neutral-8": column.isLastFrozenColumn,
      "sticky z-10": column.freeze,
    }
  )

  const cellStyle = {
    textAlign: column.align || "left",
    left: column.leftPosition,
    ...(column.style || {}),
  }

  const labelClasses = classNames("min-w-0", {
    "hover:underline cursor-pointer": canGroupTable,
  })

  return (
    <div
      ref={cellRef}
      className={cellClasses}
      style={cellStyle}
      draggable={canMoveColumn ? "true" : "false"}
      onDragStart={canMoveColumn ? handleDragStart : undefined}
      onDragEnd={canMoveColumn ? handleDragEnd : undefined}
      onDragEnter={canMoveColumn ? handleDragEnter : undefined}
      onDragLeave={canMoveColumn ? handleDragLeave : undefined}
      onDrop={canMoveColumn ? handleDrop : undefined}
      data-testid={`titlecell_${column.dataKey}`}
    >
      <div className="relative flex h-full w-full">
        <span
          className="flex w-full min-w-0 grow items-center break-words px-2 py-2 font-semibold"
          onClick={handleSort}
        >
          <span className={labelClasses}>
            <span>{column.label}</span>
          </span>
        </span>

        {isDataCol && (
          <div className="right-0 top-0 flex h-full items-center whitespace-nowrap bg-white">
            <>
              {!isGroupColumn && canGroupTable && (
                <ColumnSettings
                  column={column}
                  handleSort={handleSort}
                  onGroup={onGroup}
                  enableFrozenColumns={enableFrozenColumns}
                  isMenuOpen={isMenuOpen}
                  handleFrozenFieldChanged={onFrozenFieldChanged}
                  setIsMenuOpen={setIsMenuOpen}
                  sortColumn={sortColumn}
                  sortDesc={sortDesc}
                />
              )}
              {canMoveColumns && (
                <div
                  className="h-full w-1 cursor-ew-resize border-r-2 border-solid border-white transition-all duration-200 group-hover/row:border-neutral-6"
                  draggable="false"
                  onMouseDown={handleResizeStart}
                  onClick={stopPropagation}
                />
              )}
            </>
          </div>
        )}
      </div>
    </div>
  )
}
